import {
  Alert,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useCallback, useEffect, useState } from 'react'

import DateTimeDisplay from '../../components/DateTimeDisplay'
import HoloTable from '../../components/HoloTable'
import SearchSelect from '../../components/SearchSelect'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { useDevice } from '../../context/DeviceContext'
import useLocalStorage from '../../hooks/useLocalStorage'
import { useNavigate } from 'react-router-dom'

const ExperimentsPage = () => {
  const navigate = useNavigate()
  const [devices, setDevices] = useState([])
  const { selectedDevice, setSelectedDevice } = useDevice()
  const [experimentName, setExperimentName] = useState('')
  const [totalExperimentsCount, setTotalExperimentsCount] = useState(0)
  const [note, setNote] = useState('')
  const [runningExperiments, setRunningExperiments] = useState({})
  const [starting, setStarting] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('info')
  const [experiments, setExperiments] = useState([])
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })
  const [sortModel, setSortModel] = useState([
    { field: 'first_timestamp', sort: 'desc' },
  ])
  const [filterModel, setFilterModel] = useState({ items: [] })

  const [, setSelectedExperiment] = useLocalStorage('selectedExperiment', null)

  const deviceSlug = selectedDevice?.slug || selectedDevice?.name
  const runningName = deviceSlug ? runningExperiments[deviceSlug] : null
  const experimentIsRunning = runningName !== null && runningName !== undefined

  const setRunning = useCallback(
    (slug, name) =>
      setRunningExperiments((prev) => ({ ...prev, [slug]: name || null })),
    [],
  )

  const fetchExperiments = useCallback(
    async (page, pageSize, sortModel, filterModel) => {
      if (!selectedDevice) return

      try {
        const params = new URLSearchParams()
        params.append('page', page)
        params.append('pageSize', pageSize)

        if (sortModel.length > 0) {
          params.append('sort', sortModel[0].field)
          params.append('order', sortModel[0].sort)
        }

        filterModel.items.forEach((item) => {
          if (item.value) {
            params.append('field', item.field)
            params.append('operator', item.operator)
            params.append('value', item.value)
          }
        })

        const { data } = await fetchWithAuth(
          `devices/${selectedDevice.uuid}/experiments?${params.toString()}`,
        )

        if (data) {
          const formattedExperiments = data.data.map((exp) => ({
            id: exp._id,
            ...exp,
          }))
          setExperiments(formattedExperiments)
          setTotalExperimentsCount(data.pagination.totalItems)
        }
      } catch (error) {
        console.error('Error fetching experiments:', error)
      }
    },
    [selectedDevice],
  )

  useEffect(() => {
    fetchExperiments(
      paginationModel.page,
      paginationModel.pageSize,
      sortModel,
      filterModel,
    )
  }, [paginationModel, sortModel, filterModel, fetchExperiments])

  const fetchDevices = useCallback(async () => {
    try {
      const { data } = await fetchWithAuth('devices')
      setDevices(data.data)
    } catch (error) {
      console.error('Error fetching devices:', error)
    }
  }, [])

  useEffect(() => {
    fetchDevices()
  }, [fetchDevices])

  const handleRowClick = (row) => {
    setSelectedExperiment(row.row)
    navigate(`/data-viewer`)
  }

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      width: 300,
      sortable: true,
      filterable: true,
    },
    {
      field: 'first_timestamp',
      headerName: 'First data',
      type: 'string',
      width: 300,
      sortable: true,
      filterable: false,
      renderCell: (params) => {
        const date = new Date(Number(params.value) / 1000000)
        return <DateTimeDisplay date={date} showTime={true} />
      },
    },
    {
      field: 'last_timestamp',
      headerName: 'Last data',
      type: 'string',
      width: 300,
      sortable: true,
      filterable: false,
      renderCell: (params) => {
        const date = new Date(Number(params.value) / 1000000)
        return <DateTimeDisplay date={date} showTime={true} />
      },
    },
    {
      field: 'note',
      headerName: 'Note',
      sortable: false,
      filterable: false,
      width: 300,
    },
  ]

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }

  const handleStartExperiment = async () => {
    if (!selectedDevice) {
      showSnackbar('No device selected.', 'error')
      return
    }

    setStarting(true)

    try {
      const { data, status } = await fetchWithAuth(
        'experiments/start-experiment',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deviceSlug, experimentName, note }),
        },
      )

      if (status !== 200) {
        showSnackbar(data?.message || 'Error starting experiment.', 'error')
        return
      }

      showSnackbar('Experiment started successfully!', 'success')
      setRunning(deviceSlug, experimentName)
    } catch (error) {
      console.error('Failed to start experiment:', error)
      showSnackbar('An unexpected error occurred.', 'error')
    } finally {
      setStarting(false)
    }
  }

  const handleStopExperiment = async () => {
    if (!selectedDevice) {
      showSnackbar('No experiment is running or no device selected.', 'error')
      return
    }

    setStopping(true)

    try {
      const { data, status } = await fetchWithAuth(
        'experiments/stop-experiment',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deviceSlug, experimentName: runningName }),
        },
      )

      if (status !== 200) {
        showSnackbar(data?.message || 'Error stopping experiment.', 'error')
        return
      }

      showSnackbar(`Experiment "${runningName}" stopped successfully!`, 'success')
      setRunning(deviceSlug, null)
    } catch (error) {
      console.error('Failed to stop experiment:', error)
      showSnackbar('An unexpected error occurred.', 'error')
    } finally {
      setStopping(false)
    }
  }

  return (
    <Stack gap={3} pt={3}>
      {devices && (
        <SearchSelect
          options={devices}
          label="Device"
          selectedValue={selectedDevice}
          onSelectionChange={setSelectedDevice}
          sx={{ mr: 2, w: '100%' }}
          size="medium"
        />
      )}
      <Divider></Divider>
      <Grid container spacing={2} alignItems="start" marginBottom={5}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            id="experiment-name"
            label="Experiment Name"
            name="experimentName"
            autoComplete="experiment-name"
            value={experimentName}
            onChange={(e) => setExperimentName(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            sx={{ height: 100 }}
            id="note"
            label="Note"
            name="note"
            autoComplete="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6 }}>
          <Tooltip title={!experimentIsRunning ? 'No experiment is running on this device' : ''}>
            <span>
              <LoadingButton
                fullWidth
                variant="contained"
                color="primary"
                loading={stopping}
                loadingPosition="start"
                disabled={!experimentIsRunning}
                onClick={handleStopExperiment}
              >
                Stop Experiment
              </LoadingButton>
            </span>
          </Tooltip>
        </Grid>
        <Grid size={{ xs: 6, sm: 6 }}>
          <Tooltip title={experimentIsRunning ? `Experiment "${runningName}" is already running on this device` : ''}>
            <span>
              <LoadingButton
                fullWidth
                variant="contained"
                color="primary"
                loading={starting}
                loadingPosition="start"
                disabled={experimentIsRunning}
                onClick={handleStartExperiment}
              >
                Start Experiment
              </LoadingButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
      <Paper>
        <HoloTable
          id="experiments"
          data={experiments}
          columns={columns}
          title={`Previous Experiments for ${selectedDevice?.name || 'Selected Device'}`}
          onRowClick={handleRowClick}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          rowCount={totalExperimentsCount}
        />
      </Paper>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Stack>
  )
}

export default ExperimentsPage
